import {
  createFirmLeadSourceMappingV2,
  deleteFirmLeadSourceMappingV2,
  getFirmLeadSourceMappingsV2,
  updateFirmLeadSourceMappingV2,
} from "@/lib/data/requests/firm/firm-lead-source-mapping";
import { queryKeys } from "@/lib/data/static";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveFirmLeadSourceMappingsOptions = (leadSourceId: string) => {
  return {
    queryKey: [queryKeys.firmLeadSourceMappings, leadSourceId],
    queryFn: async () => getFirmLeadSourceMappingsV2(leadSourceId),
  } as const;
};

export const useCreateFirmLeadSourceMapping = (leadSourceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createFirmLeadSourceMappingV2>[0]) =>
      createFirmLeadSourceMappingV2(data),
    onSuccess: async () => {
      toast.success("Mapping created");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmLeadSourceMappings, leadSourceId],
      });
    },
    onError: async (error) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.firmLeadSourceMappings, leadSourceId],
        });
        toast.error(
          "This key or destination is already mapped. Refresh and retry.",
        );
        return;
      }
      toast.error("Error creating mapping");
    },
  });
};

export const useUpdateFirmLeadSourceMapping = (leadSourceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateFirmLeadSourceMappingV2>[0]) =>
      updateFirmLeadSourceMappingV2(data),
    onSuccess: async () => {
      toast.success("Mapping updated");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmLeadSourceMappings, leadSourceId],
      });
    },
    onError: async (error) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.firmLeadSourceMappings, leadSourceId],
        });
        toast.error(
          "This mapping changed or conflicts with another mapping. Refresh and retry.",
        );
        return;
      }
      toast.error("Error updating mapping");
    },
  });
};

export const useDeleteFirmLeadSourceMapping = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteFirmLeadSourceMappingV2>[0]) =>
      deleteFirmLeadSourceMappingV2(data),
    onSuccess: async (_, variables) => {
      toast.success("Mapping deleted");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.firmLeadSourceMappings, variables.leadSourceId],
      });
    },
    onError: () => toast.error("Error deleting mapping"),
  });
};
